import { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { User, LogOut, Moon, Sun, Loader2, Edit2, X, Check } from 'lucide-react';
interface UserProfileModalProps {
    onClose: () => void;
}

const AVATAR_OPTIONS = [
    // Thumbs
    { seed: "Leo", style: "thumbs" },
    { seed: "Mia", style: "thumbs" },
    { seed: "Sam", style: "thumbs" },
    { seed: "Zoe", style: "thumbs" },
    // Fun Emoji
    { seed: "Joy", style: "fun-emoji" },
    { seed: "Max", style: "fun-emoji" },
    { seed: "Lil", style: "fun-emoji" },
    { seed: "Ray", style: "fun-emoji" },
    // Avataaars Neutral
    { seed: "Alex", style: "avataaars-neutral" },
    { seed: "Bob", style: "avataaars-neutral" },
    { seed: "Cid", style: "avataaars-neutral" },
    { seed: "Dan", style: "avataaars-neutral" },
    // Bottts Neutral
    { seed: "R2", style: "bottts-neutral" },
    { seed: "C3", style: "bottts-neutral" },
    { seed: "T8", style: "bottts-neutral" },
    { seed: "B9", style: "bottts-neutral" }
];

export function UserProfileModal({ onClose }: UserProfileModalProps) {
    const { theme, setTheme } = useTheme();
    const { currentUser, logout, updateUserAvatar, updateUsername } = useAuth();
    const [loadingAvatar, setLoadingAvatar] = useState(false);
    const [isEditingAvatar, setIsEditingAvatar] = useState(false);

    const [isEditingUsername, setIsEditingUsername] = useState(false);
    const [tempUsername, setTempUsername] = useState('');
    const [loadingUsername, setLoadingUsername] = useState(false);

    const handleSaveUsername = async () => {
        if (!tempUsername.trim() || !currentUser) {
            setIsEditingUsername(false);
            return;
        }
        setLoadingUsername(true);
        try {
            await updateUsername(tempUsername.trim());
        } catch (err: any) {
            alert(err.message || 'Failed to update username');
        } finally {
            setLoadingUsername(false);
            setIsEditingUsername(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm transition-all">
            <div className="w-full max-w-sm bg-background border-4 border-border neo-shadow-lg p-6 relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-2xl font-black hover:text-primary transition-colors cursor-pointer w-8 h-8 flex items-center justify-center"
                >
                    &times;
                </button>

                <h2 className="text-3xl font-black uppercase mb-6 tracking-tight">Profile</h2>

                {!currentUser ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-4">
                        <p className="font-bold uppercase tracking-widest text-sm">Not Signed In</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-6">
                        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 p-4 border-4 border-border bg-secondary/10">
                            <div className="relative group cursor-pointer shrink-0" onClick={() => setIsEditingAvatar(!isEditingAvatar)}>
                                <div className="w-20 h-20 sm:w-16 sm:h-16 bg-[#00d0ff] border-4 border-border flex items-center justify-center neo-shadow">
                                    {currentUser.photoURL ? (
                                        <img src={currentUser.photoURL} alt="Avatar" className="w-full h-full object-contain p-1" />
                                    ) : (
                                        <User size={32} className="text-black" />
                                    )}
                                </div>
                                {/* Edit Icon Badge (Always visible on mobile, hover on desktop) */}
                                <div className="absolute -bottom-2 -right-2 flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                    <div className="bg-foreground text-background p-1.5 neo-border shadow-sm">
                                        {isEditingAvatar ? <X size={14} strokeWidth={3} /> : <Edit2 size={14} strokeWidth={3} />}
                                    </div>
                                </div>
                            </div>
                            <div className="min-w-0 flex-1 w-full text-center sm:text-left flex flex-col items-center sm:items-start">
                                {isEditingUsername ? (
                                    <div className="flex items-center justify-center sm:justify-start gap-2 w-full max-w-[200px] sm:max-w-none">
                                        <input 
                                            type="text" 
                                            value={tempUsername} 
                                            onChange={e => setTempUsername(e.target.value)}
                                            className="w-full text-sm sm:text-base font-black bg-background border-2 border-border p-1 focus:outline-none focus:border-primary uppercase tracking-wider min-w-0"
                                            autoFocus
                                            onKeyDown={e => e.key === 'Enter' && handleSaveUsername()}
                                        />
                                        <button 
                                            onClick={handleSaveUsername} 
                                            disabled={loadingUsername}
                                            className="p-1.5 bg-primary text-primary-foreground neo-border hover:-translate-y-0.5 transition-transform disabled:opacity-50 flex-shrink-0"
                                        >
                                            {loadingUsername ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} strokeWidth={3} />}
                                        </button>
                                        <button 
                                            onClick={() => setIsEditingUsername(false)} 
                                            disabled={loadingUsername}
                                            className="p-1.5 bg-muted text-muted-foreground neo-border hover:-translate-y-0.5 transition-transform disabled:opacity-50 flex-shrink-0"
                                        >
                                            <X size={16} strokeWidth={3} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center sm:justify-start gap-2 group/name max-w-full">
                                        <h3 className="text-lg sm:text-xl font-black uppercase tracking-wider truncate">{currentUser.displayName || 'Unknown User'}</h3>
                                        <button 
                                            onClick={() => {
                                                setTempUsername(currentUser.displayName || '');
                                                setIsEditingUsername(true);
                                            }}
                                            className="opacity-100 sm:opacity-0 sm:group-hover/name:opacity-100 transition-opacity p-1 hover:text-primary shrink-0"
                                        >
                                            <Edit2 size={14} strokeWidth={3} />
                                        </button>
                                    </div>
                                )}
                                <p className="text-sm font-bold opacity-80 truncate">{currentUser.email}</p>
                            </div>
                        </div>

                        {/* Avatar Selector */}
                        {isEditingAvatar && (
                            <div className="p-4 bg-background border-4 border-border neo-shadow">
                                <h4 className="font-bold uppercase text-xs tracking-widest mb-3 flex items-center justify-between">
                                    Change Avatar
                                    {loadingAvatar && <Loader2 size={14} className="animate-spin opacity-50" />}
                                </h4>
                                <div className="grid grid-cols-4 gap-2 sm:gap-3 mb-2 h-48 overflow-y-auto pr-1 custom-scrollbar">
                                    {AVATAR_OPTIONS.map((opt, i) => {
                                        const url = `https://api.dicebear.com/7.x/${opt.style}/svg?seed=${opt.seed}`;
                                        const isSelected = currentUser.photoURL === url;
                                        return (
                                            <button
                                                key={i}
                                                disabled={loadingAvatar}
                                                onClick={async () => {
                                                    setLoadingAvatar(true);
                                                    await updateUserAvatar(url);
                                                    setLoadingAvatar(false);
                                                    setIsEditingAvatar(false);
                                                }}
                                                className={`aspect-square bg-[#00d0ff] border-2 transition-transform hover:scale-105 ${isSelected ? 'border-primary neo-shadow' : 'border-border'}`}
                                            >
                                                <img src={url} alt={opt.seed} className="w-full h-full object-contain p-1" />
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="space-y-4">


                            <div className="flex items-center justify-between p-4 border-4 border-border hover:-translate-y-1 transition-transform relative group cursor-pointer bg-background">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setTheme(theme === 'dark' ? 'light' : 'dark');
                                    }}
                                    className="flex items-center gap-2 bg-foreground text-background px-4 py-2 font-black uppercase text-xs neo-border neo-hover neo-active"
                                >
                                    {theme === 'dark' ? <><Sun size={14} strokeWidth={3} /> Light</> : <><Moon size={14} strokeWidth={3} /> Dark</>}
                                </button>
                            </div>

                            <button
                                onClick={async () => {
                                    await logout();
                                    onClose();
                                }}
                                className="w-full flex items-center justify-between p-4 border-4 border-border hover:-translate-y-1 transition-transform cursor-pointer bg-primary text-primary-foreground neo-shadow"
                            >
                                <span className="font-black uppercase flex items-center gap-2">
                                    <LogOut size={20} /> Sign Out
                                </span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
